/**
 * Pricing Page
 * 
 * Standalone pricing page that handles two flows:
 * 1. OAuth Signup Flow - User tried to sign in but has no account
 *    - Shows context: "You don't have an account yet"
 *    - Displays their email from JWT
 *    - Handles plan selection to complete signup
 * 
 * 2. Normal Visit - User browsing pricing
 *    - Shows pricing grid with signup CTAs
 *    - Buttons navigate to signup page
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Loader2, 
  AlertCircle, 
  Mail, 
  ArrowRight,
  LogIn
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import PricingSection from '../components/pricing/PricingSection';
import { authApi } from '../services/api';
import { usePricingGrid } from '../hooks/usePricingGrid';
import { toast } from 'sonner';

// Session storage key for OAuth signup token
const OAUTH_SIGNUP_TOKEN_KEY = 'oauth_signup_token';
const OAUTH_SIGNUP_EMAIL_KEY = 'oauth_signup_email';
const OAUTH_SIGNUP_NAME_KEY = 'oauth_signup_name';

/**
 * Decode JWT payload (for display only - validation happens on backend)
 */
const decodeJwtPayload = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      email: payload.email,
      name: payload.name,
      provider: payload.provider,
      exp: payload.exp,
    };
  } catch (e) {
    console.error('Failed to decode JWT:', e);
    return null;
  }
};

/**
 * Check if JWT is expired
 */
const isTokenExpired = (token) => {
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) return true;
  return Date.now() >= payload.exp * 1000;
};

const PricingPage = () => {
  const navigate = useNavigate();
  const { getSignupPricingInfo } = usePricingGrid();
  
  // OAuth signup state
  const [oauthToken, setOauthToken] = useState(null);
  const [oauthEmail, setOauthEmail] = useState(null);
  const [oauthName, setOauthName] = useState(null);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [tokenError, setTokenError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Ref to prevent double-processing due to React StrictMode or URL changes
  const hasProcessedToken = useRef(false);

  // Initialize OAuth state from URL flag + sessionStorage token
  useEffect(() => {
    // Prevent double-processing
    if (hasProcessedToken.current) {
      return;
    }
    hasProcessedToken.current = true;
    
    // Check for new_signup flag (simple, reliable)
    const rawParams = new URLSearchParams(window.location.search);
    const isNewSignup = rawParams.get('new_signup') === 'true';
    
    // Get token from sessionStorage (stored by AuthCallback before redirect)
    const storedToken = sessionStorage.getItem(OAUTH_SIGNUP_TOKEN_KEY);
    
    console.log('[PricingPage] isNewSignup:', isNewSignup);
    console.log('[PricingPage] hasStoredToken:', !!storedToken);
    
    if (isNewSignup || storedToken) {
      if (!storedToken) {
        console.log('[PricingPage] new_signup flag but no token in storage');
        setTokenError('Signup session not found. Please try signing in again.');
        setIsInitialized(true);
        return;
      }
      
      // Validate token
      if (isTokenExpired(storedToken)) {
        console.log('[PricingPage] Token is expired');
        sessionStorage.removeItem(OAUTH_SIGNUP_TOKEN_KEY);
        sessionStorage.removeItem(OAUTH_SIGNUP_EMAIL_KEY);
        sessionStorage.removeItem(OAUTH_SIGNUP_NAME_KEY);
        setTokenError('Your signup session has expired. Please try signing in again.');
        setIsInitialized(true);
        return;
      }
      
      const payload = decodeJwtPayload(storedToken);
      if (!payload || !payload.email) {
        console.log('[PricingPage] Token payload invalid');
        setTokenError('Invalid signup session. Please try signing in again.');
        setIsInitialized(true);
        return;
      }
      
      console.log('[PricingPage] Setting up OAuth flow for:', payload.email);
      
      // Store email/name (may not be stored yet on fresh redirect)
      sessionStorage.setItem(OAUTH_SIGNUP_EMAIL_KEY, payload.email);
      sessionStorage.setItem(OAUTH_SIGNUP_NAME_KEY, payload.name || '');
      
      // Set state FIRST
      setOauthToken(storedToken);
      setOauthEmail(payload.email);
      setOauthName(payload.name || '');
      setIsInitialized(true);
      
      // Clean URL AFTER state is set - delay to next tick
      if (isNewSignup) {
        setTimeout(() => {
          window.history.replaceState({}, '', window.location.pathname);
        }, 100);
      }
      
      toast.info('Please select a plan to complete your signup.', { duration: 5000 });
      
    } else {
      // Normal pricing page visit
      console.log('[PricingPage] Normal pricing page visit');
      setIsInitialized(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - run once on mount only

  // Handle plan selection for OAuth signup
  const handleOAuthPlanSelect = async (plan) => {
    if (!oauthToken) return;
    
    setOauthLoading(true);
    
    try {
      const pricingInfo = getSignupPricingInfo();
      
      const response = await authApi.completeOAuthSignup(
        oauthToken,
        plan.plan_id,
        pricingInfo.version_id,
        pricingInfo.variant
      );
      
      const data = response.data;
      
      // Clear OAuth session storage
      sessionStorage.removeItem(OAUTH_SIGNUP_TOKEN_KEY);
      sessionStorage.removeItem(OAUTH_SIGNUP_EMAIL_KEY);
      sessionStorage.removeItem(OAUTH_SIGNUP_NAME_KEY);
      
      // Store signup state
      sessionStorage.setItem('is_new_signup', 'true');
      
      // Redirect to Stripe checkout for paid plans
      if (data.checkout_url) {
        toast.success('Account created! Redirecting to payment setup...');
        window.location.href = data.checkout_url;
        return;
      }
      
      // Free plan or no payment needed - go to onboarding
      toast.success('Account created successfully!');
      navigate('/onboarding', {
        replace: true,
        state: { 
          user: data.user, 
          organization: data.organization 
        }
      });
      
    } catch (err) {
      console.error('OAuth signup completion error:', err);
      const message = err.response?.data?.detail || 'Failed to complete signup. Please try again.';
      toast.error(message);
      
      // Clear OAuth state on certain errors
      if (err.response?.status === 400 || err.response?.status === 409) {
        sessionStorage.removeItem(OAUTH_SIGNUP_TOKEN_KEY);
        sessionStorage.removeItem(OAUTH_SIGNUP_EMAIL_KEY);
        sessionStorage.removeItem(OAUTH_SIGNUP_NAME_KEY);
        setOauthToken(null);
        setOauthEmail(null);
        setOauthName(null);
        
        if (err.response?.status === 409) {
          setTokenError('An account with this email already exists. Please sign in instead.');
        }
      }
    } finally {
      setOauthLoading(false);
    }
  };

  // Clear OAuth flow and start over
  const handleStartOver = () => {
    sessionStorage.removeItem(OAUTH_SIGNUP_TOKEN_KEY);
    sessionStorage.removeItem(OAUTH_SIGNUP_EMAIL_KEY);
    sessionStorage.removeItem(OAUTH_SIGNUP_NAME_KEY);
    setOauthToken(null);
    setOauthEmail(null);
    setOauthName(null);
    setTokenError(null);
    navigate('/login');
  };

  // Show loading while initializing
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#030303]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  // Determine if this is OAuth signup flow
  const isOAuthFlow = !!oauthToken && !tokenError;

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      {/* Header */}
      <header className="py-6 px-6 border-b border-white/5 bg-black/50 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
              <span className="text-white font-bold text-sm">SR</span>
            </div>
            <span className="text-lg font-semibold text-white tracking-tight">
              StackRanked
            </span>
          </Link>
          
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-gray-400 hover:text-white hover:bg-white/5">
                <LogIn className="w-4 h-4 mr-2" />
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* OAuth Signup Context Banner */}
      {isOAuthFlow && (
        <div className="py-8 px-6 bg-gradient-to-r from-purple-900/20 to-pink-900/20 border-b border-purple-500/20">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 bg-purple-500/20">
              <Mail className="w-8 h-8 text-purple-400" />
            </div>
            
            <h1 className="text-2xl md:text-3xl font-bold mb-3 text-white">
              Almost there!
            </h1>
            
            <p className="text-lg mb-2 text-gray-300">
              We don't have an account for{' '}
              <span className="font-semibold text-purple-400">
                {oauthEmail}
              </span>
            </p>
            
            <p className="text-base text-gray-400">
              Select a plan below to create your account and get started.
            </p>
            
            <button 
              onClick={handleStartOver}
              className="mt-4 text-sm underline text-gray-500 hover:text-gray-300"
            >
              Use a different email
            </button>
          </div>
        </div>
      )}

      {/* Token Error State */}
      {tokenError && (
        <div className="py-12 px-6 bg-red-900/10">
          <div className="max-w-xl mx-auto">
            <Card className="p-8 text-center bg-[#0a0a0a] border-red-500/30">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 bg-red-500/20">
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
              
              <h2 className="text-xl font-semibold mb-3 text-white">
                Signup Session Issue
              </h2>
              
              <p className="mb-6 text-gray-400">
                {tokenError}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button 
                  onClick={handleStartOver}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Try Signing In Again
                </Button>
                
                <Link to="/signup">
                  <Button variant="outline" className="border-white/20 text-gray-300 hover:bg-white/5">
                    Create Account with Email
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Loading Overlay for OAuth submission */}
      {oauthLoading && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <Card className="p-8 text-center bg-[#0a0a0a] border-white/10">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-purple-500" />
            <p className="text-gray-300">
              Creating your account...
            </p>
          </Card>
        </div>
      )}

      {/* Pricing Grid */}
      <PricingSection 
        safeMode={false} 
        onPlanSelect={isOAuthFlow ? handleOAuthPlanSelect : undefined}
      />

      {/* Footer CTA for non-OAuth visitors */}
      {!isOAuthFlow && !tokenError && (
        <div className="py-12 px-6 border-t border-white/5 bg-[#050505]">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-xl font-semibold mb-2 text-white">
              Already have an account?
            </h3>
            <p className="mb-4 text-gray-400">
              Sign in to access your dashboard and manage your products.
            </p>
            <Link to="/login">
              <Button variant="outline" className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10">
                Sign In
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Simple Footer */}
      <footer className="py-6 px-6 border-t border-white/5 bg-black">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-600">
            © {new Date().getFullYear()} StackRanked. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/terms" className="text-sm text-gray-500 hover:text-gray-300">
              Terms
            </Link>
            <Link to="/privacy" className="text-sm text-gray-500 hover:text-gray-300">
              Privacy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
